import { DayInputPlaceHolder } from "./DayInputPlaceholder";
import { Controller, UseFormReturn } from "react-hook-form";
import { TSiteVisitSchema } from "../../Models";
import { Label } from "@/components/ui/label";
import { Dispatch, JSX, SetStateAction, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SiteVisitTypeEnum, TenderDurationFragment } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import { Separator } from "@/components/ui/separator";
import { FormDatePicker } from "@/components/shared/form/DatePicker";
import { Checkbox } from "@/components/ui/checkbox";
import { DayInput } from "./DayInputComponent";
import { FormattedNumberInputField } from "@/components/shared";
import { cn } from "@/lib/utils";

interface SiteVisitFormInputsProps {
  form: UseFormReturn<TSiteVisitSchema>;
  tenderDuration: TenderDurationFragment;
  setUseOptionalDates?: Dispatch<SetStateAction<boolean>>;
  resetType?: boolean;
}

/**
 * Site Visit Form Inputs
 * @param {SiteVisitFormInputsProps} props
 * @returns {JSX.Element}
 */
function SiteVisitFormInputs(props: SiteVisitFormInputsProps): JSX.Element {
  const { form, tenderDuration, setUseOptionalDates, resetType } = props;
  const { t } = useTranslation(["modals", "common", "translation"]);

  const {
    control,
    getValues,
    setValue,
    trigger,
    setError,
    clearErrors,
    formState: { errors },
  } = form;

  const [optionalDates, setOptionalDates] = useState(!!(getValues("startDate") || getValues("endDate")));

  const handleOptionalDatesChange = (checked: boolean) => {
    setOptionalDates(checked);
    if (!checked) {
      setValue("startDate", undefined);
      setValue("endDate", undefined);
      setUseOptionalDates?.(false);
    } else {
      setUseOptionalDates?.(true);
    }
  };

  return (
    <>
      <div className="flex-wrap-4 items-start">
        {/* Duration */}
        <div className="flex flex-1 flex-col gap-2">
          <Label htmlFor="duration" className={cn(errors.duration && "text-red-500")}>
            {t("addsitevisit.inputs.duration.label")}
          </Label>
          <FormattedNumberInputField
            name="duration"
            control={control}
            placeholder={t("addsitevisit.inputs.duration.placeholder")}
            isInteger
            spaceBetweenSuffix
            additionalSuffix={t("addsitevisit.inputs.duration.suffix")}
          />
          {errors.duration && (
            <p className="input-error-message">
              {errors.duration.message as string}
            </p>
          )}
        </div>

        {/* Margin */}
        <div className="flex flex-1 flex-col gap-2">
          <Label htmlFor="margin" className={cn(errors.margin && "text-red-500")}>
            {t("addsitevisit.inputs.margin.label")}
          </Label>
          <FormattedNumberInputField
            name="margin"
            control={control}
            placeholder={t("addsitevisit.inputs.margin.placeholder")}
            additionalSuffix="%"
          />
          {errors.margin && (
            <p className="input-error-message">
              {errors.margin.message as string}
            </p>
          )}
        </div>

        {/* Type */}
        <div className="flex-1 flex flex-col gap-2">
          <Label htmlFor="type" className={cn(errors.type && "text-red-500")}>
            {t("addsitevisit.inputs.type.label")}
          </Label>
          <Select
            onValueChange={(value: SiteVisitTypeEnum) => {
              setValue("type", value);
              trigger("type");
            }}
            defaultValue={getValues("type")}
            key={resetType ? "reset" : "new"}
          >
            <SelectTrigger id="type">
              <SelectValue placeholder={t("addsitevisit.inputs.type.selectplaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {Object.values(SiteVisitTypeEnum).map((type) => (
                <SelectItem key={type} value={String(type)}>
                  {t("common:sitevisittype." + type.toLowerCase())}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.type && (
            <p className="input-error-message">
              {errors.type.message as string}
            </p>
          )}
        </div>
      </div>

      {/* Weekday Fields */}
      <div className="grid-2 gap-4">
        {[0, 1].map((num) => (
          <DayInputPlaceHolder key={num} />
        ))}
        {[
          "Monday", "Tuesday", "Wednesday", "Thursday",
          "Friday", "Saturday", "Sunday", "Holiday"
        ].map((day) => (
          <DayInput
            key={day}
            day={day}
            control={control}
            errors={errors} />
        ))}
      </div>

      {
        errors.mondayDay && (
          <p className="input-error-message">
            {errors.mondayDay.message as string}
          </p>
        )
      }

      {/* Optional Start and End Date */}
      <div>
        <p className="text-sm font-semibold text-slate-400">{t("common:optional")}</p>
        <Separator className="mt-2 mb-4" />

        <div className="flex items-center gap-2 mb-2">
          <Checkbox
            id="optionalDates"
            checked={optionalDates}
            onCheckedChange={handleOptionalDatesChange}
          />
          <Label htmlFor="optionalDates" className="text-xs">{t("addsitevisit.labels.optionaldates")}</Label>
        </div>

        {optionalDates && (
          <div className="mt-2">
            <p className="text-xs text-gray-400">
              {t("addsitevisit.tooltips.optionaldatesfirst")}
            </p>
            <p className="text-xs text-gray-400 mb-2">
              {t("addsitevisit.tooltips.optionaldatessecond")}
            </p>
            <div className="flex-wrap gap-4 flex justify-between">
              <div className="form-container">
                <Label htmlFor="startDate" className={cn(errors.startDate && "text-red-500")}>
                  {t("shift.inputs.startdate.label")}
                </Label>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <FormDatePicker
                      field={field}
                      allowOlderDates={true}
                      initialDate={new Date(tenderDuration.contract.startDate)}
                      minDate={new Date(tenderDuration.contract.startDate)}
                      maxDate={new Date(tenderDuration.contract.endDate)}
                      errorDisplay="form"
                      onValidationError={(message) => {
                        if (message) {
                          setError("startDate", { type: "manual", message });
                        } else {
                          clearErrors("startDate");
                        }
                      }}
                    />
                  )}
                />
                {errors.startDate && (
                  <p className="input-error-message">
                    {errors.startDate.message as string}
                  </p>
                )}
              </div>

              <div className="form-container">
                <Label htmlFor="endDate" className={cn(errors.endDate && "text-red-500")}>
                  {t("shift.inputs.enddate.label")}
                </Label>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <FormDatePicker
                      field={field}
                      allowOlderDates={true}
                      initialDate={new Date(tenderDuration.contract.endDate)}
                      minDate={new Date(tenderDuration.contract.startDate)}
                      maxDate={new Date(tenderDuration.contract.endDate)}
                      errorDisplay="form"
                      onValidationError={(message) => {
                        if (message) {
                          setError("endDate", { type: "manual", message });
                        } else {
                          clearErrors("endDate");
                        }
                      }}
                    />
                  )}
                />
                {errors.endDate && (
                  <p className="input-error-message">
                    {errors.endDate.message as string}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default SiteVisitFormInputs;
